import { StacklineTheme, useStacklineTheme } from '@stackline/ui';
import React, { useCallback } from 'react';
import { useAdMetricsData } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdvertisingSummaryMetricsData';
import { IconNoChange } from 'src/components/BeaconV2Components/icons';
import { CustomIconIncrease, CustomIconDecrease } from 'src/components/BeaconV2Components/icons';

import {
  AdvertisingMetricLabels,
  isAdSpendMetricId,
  keyMetricsAdSales,
  keyMetricsAdSpend,
  keyMetricsIncrementality,
  keyMetricsReach
} from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import { UnstyledButton } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/PromotionMetricCards';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { useHistory, useMetricFormatter } from 'src/utils/Hooks';
import { getQueryParamValue, updateUrlQueryParams, isBeaconCriteo, isInstacart } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { CampaignColor } from 'src/types/application/types';
import colors from 'src/utils/colors';

interface ChangeColor {
  changeNumber: number | undefined;
  fieldName: string;
}

/**
 * Some field names will have inverted text change color:
 * Ad Spend, CPC. CPA, CPM, ACOS - green = decrease.
 * Ad Spend, CPC. CPA, CPM, ACOS - red = increase.
 * These metrics are better when they are lower.
 */
const getChangeTextColor = (value: ChangeColor) => {
  const increaseColor = CampaignColor.OutOfBudget;
  const decreaseColor = CampaignColor.Delivering;

  if (!value.changeNumber || value.changeNumber === 0) {
    return CampaignColor.Ended;
  }
  const changeColor = value.changeNumber < 0 ? increaseColor : decreaseColor;
  return changeColor;
};

/**
 * The arrow color will be rotated 180 degrees for:
 * Ad Spend, CPC. CPA, CPM, ACOS - green = decrease.
 * Ad Spend, CPC. CPA, CPM, ACOS - red = increase.
 * These metrics are better when they are lower.
 */
const getSvgChangeIcon = (value: ChangeColor, overrideColor?: string) => {
  if (!value.changeNumber || value.changeNumber === 0) {
    return IconNoChange;
  }

  return value.changeNumber > 0 ? (
    <CustomIconIncrease color={overrideColor} />
  ) : (
    <CustomIconDecrease color={overrideColor} />
  );
};

/**
 *
 * @param textValue could be (11.12, 3.33 or a string 33.57M, 287.05K, $11.12, 13.22% etc...)
 * It could also contains non-US number such as 11,12 or 3,33
 * Account for cases with multiple decimal points: "12,345.67", "111,111.11"
 * @returns
 */
const getMainMetricTextColor = (formattedValue: string | undefined) => {
  if (!formattedValue) {
    return CampaignColor.Ended;
  }

  // Normalize decimal separator (convert ',' to '.' if needed)
  const normalizedValue = formattedValue.replace(',', '.');

  // Extract only valid numeric characters, ensuring we respect all decimal points. Eg: "12,345.67" -> "12345.67"
  const trimmedValue = normalizedValue.match(/-?\d+(\.\d+)?/g)?.[0] || '0';

  const floatValue = parseFloat(trimmedValue);

  if (Number.isNaN(floatValue) || floatValue === 0) {
    return CampaignColor.Ended;
  }
  return colors.darkBlue; // Default color
};

const MetricCard = ({
  changeNumber,
  label,
  theme,
  selected,
  isLoading = true,
  formattedValue,
  formattedChanges,
  formattedDiff,
  isAdSpend,
  onClick
}: {
  changeNumber?: number;
  label: string;
  theme: StacklineTheme;
  selected: boolean;
  isLoading: boolean;
  formattedValue: string | number;
  formattedChanges: string | number;
  formattedDiff?: string | number;
  isAdSpend?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}) => {
  const isCriteoOrInstacart = isBeaconCriteo() || isInstacart(getQueryParamValue('rid'));

  const overrideColor = isAdSpend ? colors.graysSecondaryText : undefined;

  const ChangeIcon = () => {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          marginRight: '4px',
          marginLeft: '8px',
          position: 'relative',
          top: '3px'
        }}
      >
        {getSvgChangeIcon({
          changeNumber,
          fieldName: label
        }, overrideColor)}
      </div>
    );
  };

  return (
    <UnstyledButton
      onClick={onClick}
      stacklineTheme={theme}
      selected={selected}
      sx={{
        transform: 'translateX(-12px)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: isCriteoOrInstacart ? '320px' : '254px',
        boxSizing: 'border-box',
        marginRight: '-12px',
        height: '80px',
        padding: '12px 12px 8px 12px'
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '20px',
          textAlign: 'left',
          whiteSpace: 'break-spaces'
        }}
      >
        <Text sx={{ height: '16px' }} color="secondary" variant="subtitle3" transform="uppercase">
          {label}
        </Text>
      </div>
      <Flex height="32px" justifyContent="space-between" alignItems="center">
        {isLoading ? (
          <SlSkeleton width="55px" height="34px" />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <Text variant="body0" sx={{ color: getMainMetricTextColor(`${formattedValue}`) }}>
              {formattedValue}
            </Text>{' '}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <ChangeIcon />
              <Text
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: overrideColor || getChangeTextColor({
                    changeNumber,
                    fieldName: label
                  })
                }}
              >
                {formattedDiff} {`(${formattedChanges})`}
              </Text>
            </div>
          </div>
        )}
      </Flex>
    </UnstyledButton>
  );
};

const AdvertisingSummaryMetricsCards = () => {
  const theme = useStacklineTheme();
  const history = useHistory();
  const formatMetric = useMetricFormatter();
  const isCriteoOrInstacart = isBeaconCriteo() || isInstacart(getQueryParamValue('rid'));

  const { keyMetricsCardData, keyMetricsCompareData, keyMetricsCardChanges, loading } = useAdMetricsData();

  const handleCardClick = useCallback(
    (selectedMetric: string) => {
      history.push(
        updateUrlQueryParams({
          selectedMetric
        })
      );
    },
    [history]
  );

  return (
    <Flex justifyContent="space-between" gap={12}>
      {/* Reach */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Reach</Text>
        </Flex>
        {keyMetricsReach.map((metric) => (
          <MetricCard
            key={metric.id}
            label={metric.label}
            theme={theme}
            selected={getQueryParamValue('selectedMetric') === metric.id}
            isLoading={loading}
            isAdSpend={isAdSpendMetricId(metric.id)}
            formattedValue={
              keyMetricsCardData
                ? formatMetric(keyMetricsCardData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedDiff={
              keyMetricsCompareData
                ? formatMetric(keyMetricsCardData[metric.id] - keyMetricsCompareData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedChanges={
              keyMetricsCardChanges
                ? formatMetric(keyMetricsCardChanges[metric.id], METRICTYPE.PERCENT, {
                    decimalPlaces: 1,
                    showFullValue: true,
                    percentPlus: true
                  })
                : '0'
            }
            changeNumber={keyMetricsCardChanges[metric.id]}
            onClick={() => handleCardClick(metric.id)}
          />
        ))}
      </Flex>

      {/* Ad Spend */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Ad Spend</Text>
        </Flex>
        {keyMetricsAdSpend.map((metric) => (
          <MetricCard
            key={metric.id}
            label={metric.label}
            theme={theme}
            selected={getQueryParamValue('selectedMetric') === metric.id}
            isLoading={loading}
            isAdSpend={isAdSpendMetricId(metric.id)}
            formattedValue={
              keyMetricsCardData
                ? formatMetric(keyMetricsCardData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedDiff={
              keyMetricsCompareData
                ? formatMetric(keyMetricsCardData[metric.id] - keyMetricsCompareData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedChanges={
              keyMetricsCardChanges
                ? formatMetric(keyMetricsCardChanges[metric.id], METRICTYPE.PERCENT, {
                    decimalPlaces: 1,
                    showFullValue: true,
                    percentPlus: true
                  })
                : '0'
            }
            changeNumber={keyMetricsCardChanges[metric.id]}
            onClick={() => handleCardClick(metric.id)}
          />
        ))}
      </Flex>

      {/* Ad Sales */}
      <Flex flexDirection="column">
        <Flex marginBottom="12px">
          <Text variant="h4">Ad Sales</Text>
        </Flex>
        {keyMetricsAdSales.map((metric) => (
          <MetricCard
            key={metric.id}
            label={metric.label}
            theme={theme}
            selected={getQueryParamValue('selectedMetric') === metric.id}
            isLoading={loading}
            isAdSpend={isAdSpendMetricId(metric.id)}
            formattedValue={
              keyMetricsCardData
                ? formatMetric(keyMetricsCardData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedDiff={
              keyMetricsCompareData
                ? formatMetric(keyMetricsCardData[metric.id] - keyMetricsCompareData[metric.id], metric.metricType, {
                    decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                    showFullValue: metric?.metricType === METRICTYPE.PERCENT
                  })
                : '0'
            }
            formattedChanges={
              keyMetricsCardChanges
                ? formatMetric(keyMetricsCardChanges[metric.id], METRICTYPE.PERCENT, {
                    decimalPlaces: 1,
                    showFullValue: true,
                    percentPlus: true
                  })
                : '0'
            }
            changeNumber={keyMetricsCardChanges[metric.id]}
            onClick={() => handleCardClick(metric.id)}
          />
        ))}
      </Flex>

      {/* Incrementality */}
      {!isCriteoOrInstacart && (
        <Flex flexDirection="column">
          <Flex marginBottom="12px">
            <Text variant="h4">Incrementality</Text>
          </Flex>
          {keyMetricsIncrementality.map((metric) => (
            <MetricCard
              key={metric.id}
              label={metric.label}
              theme={theme}
              selected={getQueryParamValue('selectedMetric') === metric.id}
              isLoading={loading}
              isAdSpend={isAdSpendMetricId(metric.id)}
              formattedValue={
                keyMetricsCardData
                  ? formatMetric(keyMetricsCardData[metric.id], metric.metricType, {
                      decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                      showFullValue: metric?.metricType === METRICTYPE.PERCENT
                    })
                  : '0'
              }
              formattedDiff={
                keyMetricsCompareData
                  ? formatMetric(keyMetricsCardData[metric.id] - keyMetricsCompareData[metric.id], metric.metricType, {
                      decimalPlaces: metric?.metricType === METRICTYPE.PERCENT ? 1 : 2,
                      showFullValue: metric?.metricType === METRICTYPE.PERCENT
                    })
                  : '0'
              }
              formattedChanges={
                keyMetricsCardChanges
                  ? formatMetric(keyMetricsCardChanges[metric.id], METRICTYPE.PERCENT, {
                      decimalPlaces: 1,
                      showFullValue: true,
                      percentPlus: true
                    })
                  : '0'
              }
              changeNumber={keyMetricsCardChanges[metric.id]}
              onClick={() => handleCardClick(metric.id)}
            />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default AdvertisingSummaryMetricsCards;
